/**
 * Регулярное выражение для нативно tabbable HTML-элементов.
 */
const TABBABLE_NODES = /input|select|textarea|button|object/;

/**
 * CSS-селектор всех потенциально фокусируемых элементов.
 */
export const FOCUS_SELECTOR =
  'a, input, select, textarea, button, object, [tabindex]';

/**
 * Проверяет скрыт ли элемент через inline display:none.
 * В тестовой среде всегда возвращает false.
 */
function hidden(element: HTMLElement) {
  if (process.env.NODE_ENV === 'test') {
    return false;
  }

  return element.style.display === 'none';
}

/**
 * Проверяет, является ли элемент визуально доступным для фокуса.
 * Учитывает aria-hidden, hidden, type="hidden"
 * и скрытых родителей.
 */
function visible(element: HTMLElement) {
  const isHidden =
    element.getAttribute('aria-hidden') ||
    element.getAttribute('hidden') ||
    element.getAttribute('type') === 'hidden';

  if (isHidden) {
    return false;
  }

  let parentElement: HTMLElement = element;

  while (parentElement) {
    if (parentElement === document.body || parentElement.nodeType === 11) {
      break;
    }

    if (hidden(parentElement)) {
      return false;
    }

    parentElement = parentElement.parentNode as HTMLElement;
  }

  return true;
}

function getElementTabIndex(element: HTMLElement) {
  let tabIndex: string | null | undefined = element.getAttribute('tabindex');

  if (tabIndex === null) {
    tabIndex = undefined;
  }

  return parseInt(tabIndex as string, 10);
}

/**
 * Проверяет, поддерживает ли элемент свойство disabled
 */
function isDisableable(
  element: HTMLElement
): element is
  | HTMLButtonElement
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement
  | HTMLOptGroupElement
  | HTMLOptionElement
  | HTMLFieldSetElement {
  return 'disabled' in element;
}

/**
 * Проверяет, может ли элемент получить фокус программно.
 * Учитывает disabled, href у ссылок и tabindex.
 */
export function focusable(element: HTMLElement) {
  const nodeName = element.nodeName.toLowerCase();
  const isTabIndexNotNaN = !Number.isNaN(getElementTabIndex(element));

  const isNativeFocusable =
    TABBABLE_NODES.test(nodeName) &&
    (!isDisableable(element) || !element.disabled);

  const res =
    isNativeFocusable ||
    (element instanceof HTMLAnchorElement
      ? Boolean(element.href) || isTabIndexNotNaN
      : isTabIndexNotNaN);

  return res && visible(element);
}

/**
 * Проверяет, участвует ли элемент в Tab-навигации.
 * tabindex < 0 исключается из таб-цикла.
 */
export function tabbable(element: HTMLElement) {
  const tabIndex = getElementTabIndex(element);
  const isTabIndexNaN = Number.isNaN(tabIndex);

  return (isTabIndexNaN || tabIndex >= 0) && focusable(element);
}

/**
 * Возвращает все tabbable-элементы внутри контейнера
 * в порядке DOM.
 */
export function findTabbableDescendants(element: HTMLElement): HTMLElement[] {
  return Array.from(
    element.querySelectorAll<HTMLElement>(FOCUS_SELECTOR)
  ).filter(tabbable);
}
